import {ChangeDetectionStrategy, Component, computed, Injectable, input, Signal, viewChild} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {ArkivMappenodeDLM} from "felles-ng2/tjenester/datalager/ArkivDatatjeneste";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {TittelVerdiKomponent, VerdiKomponent} from "web-ng2/delt/komponenter/tittel-verdi/tittel-verdi";
import {TranslateModule} from "@ngx-translate/core";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {Observable, of, switchMap} from "rxjs";
import {ArkivnodeId, SkjermingDetaljerModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {HtmlFormatertKomponent} from "web-ng2/delt/komponenter/html-formatert/html-formatert";
import {ArkivmappeMenyKomponent, ArkivmappeMenyKomponentAvhengigheter} from "web-ng2/moduler/arkiv/komponenter/arkivmappe-meny/arkivmappe-meny";
import {MatMenuTrigger} from "@angular/material/menu";
import {SkjermingVisKomponent} from "web-ng2/moduler/skjerming/komponenter/skjerming-vis/skjerming-vis";
import {SkjermetEntitetType, SkjermingDatatjeneste} from "felles-ng2/tjenester/datalager/SkjermingDatatjeneste";
import {tomSkjermingDetaljerModell} from "felles-ng2/modell/tommeModeller";
import {erArkivmappeSkjermet, kanArkivnodeRedigeres} from "felles-ng2/modell/arkivModellHjelpere";
import {mapTilData, terminerEtterFørsteRequestRespons} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {SkjermingDialogKomponent} from "web-ng2/moduler/skjerming/modale/skjerming-dialog/skjerming-dialog";
import {MatDialog} from "@angular/material/dialog";

const debug = initDebugger('ArkivmappeDetaljerKomponent');

/**
 * Viser detaljer for valgt arkivmappe
 */
@Component({
    selector: 'arkivmappe-detaljer',
    templateUrl: 'arkivmappe-detaljer.html',
    styleUrl: 'arkivmappe-detaljer.scss',
    imports: [
        MatButton,
        MatIcon,
        MatTooltip,
        TittelVerdiKomponent,
        TranslateModule,
        VerdiKomponent,
        HtmlFormatertKomponent,
        ArkivmappeMenyKomponent,
        MatIconButton,
        MatMenuTrigger,
        SkjermingVisKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArkivmappeDetaljerKomponent {

    private skjermingBlokk = viewChild<TittelVerdiKomponent>('skjermingBlokk');

    mappe = input.required<ArkivMappenodeDLM>();

    readonly skjerming: Signal<SkjermingDetaljerModell>;

    skjermetEntitetType = SkjermetEntitetType.arkivmappe;
    redigerbar = computed(() => kanArkivnodeRedigeres(this.mappe(), this.kontekst.autentisertBruker()));

    constructor(private avhengigheter: ArkivmappeDetaljerKomponentAvhengigheter, private kontekst: BrukerkontekstTilbyder, private matDialog: MatDialog) {
        const mappeSomObserverbar = toObservable(this.mappe).pipe(takeUntilDestroyed());

        this.skjerming = toSignal(mappeSomObserverbar
            .pipe(
              //kun laste skjermingsinfo dersom mappen er skjermet
              switchMap(arkivmappe => erArkivmappeSkjermet(arkivmappe) ?
                this.avhengigheter.skjermingsdetaljer(arkivmappe.id) :
                of(tomSkjermingDetaljerModell()))),
          {initialValue: tomSkjermingDetaljerModell()});

        //sørger for at fokusert blokk forsvinner når man endrer hvilken mappe man ser på
        mappeSomObserverbar.subscribe(() => this.resetFokusertBlokk());
    }

    /** scroller til skjerming-blokken og gir denne visuelt fokus */
    fokuserSkjerming() {
        this.skjermingBlokk()?.fokuser();
    }

    redigerSkjerming() {
        SkjermingDialogKomponent.redigerForArkivmappe(this.matDialog, this.mappe());
    }

    //private hjelpemetoder

    private resetFokusertBlokk() {
        this.skjermingBlokk()?.fokuser(false);
    }
}

@Injectable()
export class ArkivmappeDetaljerKomponentAvhengigheter {

    constructor(private skjermingDatatjeneste: SkjermingDatatjeneste) {
    }

    skjermingsdetaljer(id: ArkivnodeId): Observable<SkjermingDetaljerModell> {
        return this.skjermingDatatjeneste.hentMedMeta(id, SkjermetEntitetType.arkivmappe)
          .pipe(terminerEtterFørsteRequestRespons(), mapTilData());
    }
}
